import { fetchJSON as fetchAPI } from './client';

export type GovernanceDashboardSnapshot = {
  tenant_id: string; trust_score: number; explainability_pct: number;
  evidence_coverage_pct: number; decision_accuracy_pct: number;
  certified_count: number; pending_review: number; override_count: number;
  transparency_index: number; generated_at: string;
};

export type ExplainabilityReport = {
  recommendation_id: string; why: string; why_now: string; why_this_action: string;
  evidence: string[]; assumptions: string[]; risks: string[]; confidence: number;
  alternatives: string[]; expected_impact: string; historical_proof: string;
};

export type EvidenceNode = {
  id: string; type: string; label: string; category: string; source: string; strength: string;
};

export type EvidenceGraphSnapshot = {
  recommendation_id: string; nodes: EvidenceNode[]; edge_count: number; coverage_pct: number;
};

export type LineageStep = {
  step: number; stage: string; source: string; detail: string; timestamp: string;
};

export type DecisionLineage = {
  recommendation_id: string; steps: LineageStep[]; complete: boolean;
};

export type CounterfactualScenario = {
  id: string; scenario: string; outcome: string; confidence: number;
  low_bound?: string; high_bound?: string;
};

export type CertificationRecord = {
  recommendation_id: string; governance_status: string; evidence_status: string;
  validation_status: string; quality_status: string; safety_status: string;
  certification_score: number; certified_at: string;
};

export type RiskAssessment = {
  recommendation_id: string; overall_risk: string; financial_risk: string;
  inventory_risk: string; compliance_risk: string; operational_risk: string;
  brand_risk: string; customer_impact: string; assessed_at: string;
};

export type AuditEntry = {
  id: string; recommendation_id: string; action: string; actor: string;
  engine?: string; model_version?: string; evidence_used?: string[];
  outcome?: string; timestamp: string;
};

export type TransparencySnapshot = {
  explainability_coverage: number; lineage_coverage: number; evidence_coverage: number;
  certification_coverage: number; risk_coverage: number; overall_index: number;
};

const COMMAND_BASE = import.meta.env.VITE_COMMANDCENTER_API ?? '/api/v1/command';
const fetchC = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${COMMAND_BASE}${path}`, init);

export const aiGovernanceApi = {
  dashboard: () => fetchC<GovernanceDashboardSnapshot>('/ai-governance/dashboard'),
  explain: (id: string) => fetchC<ExplainabilityReport>(`/ai-governance/explain/${id}`),
  evidence: (id: string) => fetchC<EvidenceGraphSnapshot>(`/ai-governance/evidence/${id}`),
  lineage: (id: string) => fetchC<DecisionLineage>(`/ai-governance/lineage/${id}`),
  counterfactuals: (recommendationId: string) =>
    fetchC<{ scenarios: CounterfactualScenario[] }>('/ai-governance/counterfactual', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recommendation_id: recommendationId }),
    }).then((r) => r.scenarios),
  certification: (id: string) => fetchC<CertificationRecord>(`/ai-governance/certification/${id}`),
  risk: (id: string) => fetchC<RiskAssessment>(`/ai-governance/risk/${id}`),
  audit: () => fetchC<{ entries: AuditEntry[] }>('/ai-governance/audit').then((r) => r.entries),
  transparency: () => fetchC<TransparencySnapshot>('/ai-governance/transparency'),
};
